import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

// Import all components
import ProfileSection from './components/ProfileSection';
import SecuritySection from './components/SecuritySection';
import NotificationSection from './components/NotificationSection';
import AppConfigSection from './components/AppConfigSection';
import PrivacySection from './components/PrivacySection';
import SettingsNavigation from './components/SettingsNavigation';
import Breadcrumb from './components/Breadcrumb';

const AccountSettings = () => {
  const [activeSection, setActiveSection] = useState('profile');
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  // Handle mobile navigation
  const handleSectionChange = (section) => {
    setActiveSection(section);
    setIsMobileNavOpen(false);
  };

  // Close mobile nav when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileNavOpen && !event.target.closest('.mobile-nav')) {
        setIsMobileNavOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileNavOpen]);

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'profile':
        return <ProfileSection />;
      case 'security':
        return <SecuritySection />;
      case 'notifications':
        return <NotificationSection />;
      case 'app-config':
        return <AppConfigSection />;
      case 'privacy':
        return <PrivacySection />;
      default:
        return <ProfileSection />;
    }
  };

  const getSectionTitle = () => {
    const titles = {
      'profile': 'Profile Information',
      'security': 'Security Settings',
      'notifications': 'Notification Preferences',
      'app-config': 'App Configuration',
      'privacy': 'Privacy & Data'
    };
    return titles[activeSection] || 'Account Settings';
  };

  const getSectionDescription = () => {
    const descriptions = {
      'profile': 'Manage your personal details and investment preferences',
      'security': 'Configure password, two-factor authentication, and security settings',
      'notifications': 'Customize your notification preferences and alert settings',
      'app-config': 'Personalize your app experience with theme and display options',
      'privacy': 'Control your data privacy and exercise your data rights'
    };
    return descriptions[activeSection] || 'Manage your account settings and preferences';
  };

  return (
    <>
      <Helmet>
        <title>Account Settings - StockTracker Pro</title>
        <meta name="description" content="Manage your StockTracker Pro account settings, security preferences, notifications, and privacy controls." />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar />
        
        <main className="lg:ml-64 pt-16">
          <div className="container-financial py-8">
            {/* Mobile Navigation Toggle */}
            <div className="lg:hidden mb-6">
              <Button
                variant="outline"
                onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
                iconName="Menu"
                iconPosition="left"
                fullWidth
              >
                {getSectionTitle()}
              </Button>
            </div>

            {/* Mobile Navigation Overlay */}
            {isMobileNavOpen && (
              <div className="fixed inset-0 bg-black bg-opacity-50 z-dropdown lg:hidden">
                <div className="mobile-nav fixed left-0 top-16 bottom-0 w-80 bg-card border-r border-border p-6 overflow-y-auto">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-foreground">Settings</h2>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setIsMobileNavOpen(false)}
                    >
                      <Icon name="X" size={20} />
                    </Button>
                  </div>
                  <SettingsNavigation
                    activeSection={activeSection}
                    onSectionChange={handleSectionChange}
                  />
                </div>
              </div>
            )}

            <div className="flex gap-8">
              {/* Desktop Navigation Sidebar */}
              <div className="hidden lg:block w-80 flex-shrink-0">
                <div className="card-elevated p-6 sticky top-24">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Settings" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h1 className="text-lg font-semibold text-foreground">Account Settings</h1>
                      <p className="text-sm text-muted-foreground">Manage your preferences</p>
                    </div>
                  </div>
                  
                  <SettingsNavigation
                    activeSection={activeSection}
                    onSectionChange={setActiveSection}
                  />
                </div>
              </div>

              {/* Main Content Area */}
              <div className="flex-1 min-w-0">
                <Breadcrumb activeSection={activeSection} />
                
                {/* Section Header */}
                <div className="mb-8">
                  <h1 className="text-2xl font-bold text-foreground mb-2">
                    {getSectionTitle()}
                  </h1>
                  <p className="text-muted-foreground">
                    {getSectionDescription()}
                  </p>
                </div>

                {/* Active Section Content */}
                <div className="space-y-6">
                  {renderActiveSection()}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default AccountSettings;